import { useI18n } from "vue-i18n"

export type DateFormat = "short" | "medium" | "long" | "day" | "month-short" | "relative"

// Map the app's i18n locale ("no"/"en") to a full BCP-47 tag for Intl.
// Exported so ad-hoc Intl.*Format call sites format identically to the
// shared date/currency composables instead of passing the raw "no"/"en".
export function toDisplayLocale(i18nLocale: string): string {
  if (i18nLocale === "no") return "nb-NO"
  return "en-US"
}

function getLocale(i18nLocale: string): string {
  return toDisplayLocale(i18nLocale)
}

export function useFormatDate() {
  // Tolerate being called outside a setup() (e.g., in tests without i18n installed)
  let i18nLocale: { value: string } = { value: "en" }
  let translate: (key: string, named?: Record<string, unknown>) => string = (key) => key
  try {
    const i18n = useI18n()
    if (i18n?.locale) i18nLocale = i18n.locale as unknown as { value: string }
    if (i18n?.t) translate = i18n.t as typeof translate
  } catch {
    // No i18n context available — fall back to defaults
  }

  function toDate(input: string | Date | number | null | undefined): Date | null {
    if (input === null || input === undefined || input === "") return null
    // Bare "YYYY-MM-DD" strings (how all transaction dates are stored) parse
    // as UTC midnight via new Date(), which renders a day early west of UTC —
    // append a time so the date is interpreted in the user's local calendar.
    const d =
      input instanceof Date
        ? input
        : typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)
          ? new Date(`${input}T00:00:00`)
          : new Date(input)
    if (Number.isNaN(d.getTime())) return null
    return d
  }

  function format(input: string | Date | number | null | undefined, fmt: DateFormat = "short"): string {
    const d = toDate(input)
    if (!d) return "—"

    const tag = getLocale(i18nLocale.value)

    switch (fmt) {
      case "short":
        return d.toLocaleDateString(tag, { year: "numeric", month: "short", day: "numeric" })
      case "medium":
        return d.toLocaleDateString(tag, { year: "numeric", month: "long", day: "numeric" })
      case "long":
        return d.toLocaleDateString(tag, {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      case "day":
        return d.getDate().toString()
      case "month-short":
        return d.toLocaleDateString(tag, { month: "short" })
      case "relative":
        return formatRelative(d, tag)
    }
  }

  function formatRelative(d: Date, tag: string): string {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const target = new Date(d)
    target.setHours(0, 0, 0, 0)
    const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return translate("common.today")
    if (diffDays === 1) return translate("common.tomorrow")
    if (diffDays === -1) return translate("common.yesterday")
    if (diffDays > 0 && diffDays < 7) return translate("common.in_days", { count: diffDays })
    if (diffDays < 0 && diffDays > -7) return translate("common.days_ago", { count: Math.abs(diffDays) })

    // Beyond a week: include the year when it differs from the current one,
    // otherwise "Jul 16" is ambiguous across year boundaries.
    const opts: Intl.DateTimeFormatOptions =
      d.getFullYear() === today.getFullYear()
        ? { month: "short", day: "numeric" }
        : { year: "numeric", month: "short", day: "numeric" }
    return d.toLocaleDateString(tag, opts)
  }

  return { format, formatRelative: (d: Date | string) => format(d, "relative") }
}
